import React, { useState, useEffect } from "react";
import {
    useNavigate,
    useNavigation,
    useLoaderData,
    useParams,
    useSubmit,
    Form as ReactRouterForm,
} from "react-router";
import ScrollToHashElement from "@cascadia-code/scroll-to-hash-element";
import {
    Form,
    FloatingLabel,
    Image,
    Row,
    Col,
    Button,
    ListGroup,
    Badge,
    Stack,
} from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash, faLink } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "components/shared/ThemeProvider";
import ConfirmCancelDialog from "components/shared/ConfirmDialog";
import Breadcrumb from "components/wishlist/Breadcrumb";
import { renderSimpleToast } from "components/shared/Toasts";
import imagePlaceHolder from "assets/images/product_image_placeholder.png";
import { trackImpressions, trackClick } from "services/feedbackService";

const ProductImage = styled(Image)`
    width: 100%;
`;

const EventDetailItem = styled(ListGroup.Item)`
    color: ${(props) => (props.$isDarkMode ? "#fff" : "#000")} !important;
    text-decoration: none;
`;

const EventDetail = ({
    id,
    name,
    description,
    event_timestamp,
    link,
    code,
    data,
    isDarkMode,
    productUrl,
}) => {
    const handleClick = () => {
        const phrases = data?.matches
            ?.map((m) => m.matched_text)
            .filter(Boolean);
        if (phrases?.length > 0) {
            trackClick(phrases, id);
        }
    };

    const fragment = data?.text;
    const href = productUrl
        ? fragment && code === "PROMOTION_DETECTED"
            ? `${productUrl}#:~:text=${encodeURIComponent(fragment)}`
            : productUrl
        : link;

    return (
        <EventDetailItem
            as="a"
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="event-detail"
            $isDarkMode={isDarkMode}
            onClick={handleClick}
        >
            <h6>{name}</h6>
            <div className="text-muted">
                {new Date(event_timestamp).toLocaleString()}
            </div>
            <div>{description}</div>
        </EventDetailItem>
    );
};

const ViewDetailForm = () => {
    const { wishlistRequestId } = useParams();
    const [showDialog, setShowDialog] = useState(false);
    const data = useLoaderData() || {};
    const nav = useNavigate();
    const navigation = useNavigation();
    const submit = useSubmit();
    const { isDarkMode } = useTheme();
    const loading = navigation.state === "loading";

    useEffect(() => {
        const events = (data.item_events || [])
            .map((e) => ({
                event_id: e.id,
                phrases: (e.data?.matches || [])
                    .map((m) => m.matched_text)
                    .filter(Boolean),
            }))
            .filter((e) => e.phrases.length > 0);
        if (events.length > 0) {
            trackImpressions(events);
        }
    }, [data.item_events]);

    const handleConfirmDelete = async (event) => {
        if (event === "cancel") {
            setShowDialog(false);
        } else if (event === "confirm") {
            submit(data, { method: "delete", encType: "application/json" });
        } else {
            throw new Error(`Unknown event: ${event}`);
        }
    };

    return (
        <>
            <Breadcrumb activeLabel="Product Detail" />
            <ScrollToHashElement />
            <ReactRouterForm method="post">
                <div className="d-flex gap-2 my-3">
                    <Button
                        variant="primary"
                        disabled={loading}
                        title="Edit"
                        onClick={() =>
                            nav(`/wishlist/${wishlistRequestId}/edit`)
                        }
                    >
                        <FontAwesomeIcon icon={faPen} /> Edit
                    </Button>
                    <Button
                        variant="primary"
                        disabled={loading}
                        title="Share"
                        onClick={() => {
                            navigator.clipboard.writeText(data.product_url);
                            renderSimpleToast("Copied link to clipboard");
                        }}
                    >
                        <FontAwesomeIcon icon={faLink} /> Share
                    </Button>
                    <Button
                        variant="danger"
                        disabled={loading}
                        title="Delete"
                        onClick={() => setShowDialog(true)}
                    >
                        <FontAwesomeIcon icon={faTrash} /> Delete
                    </Button>
                </div>
                <Row className="mb-3">
                    <Form.Group as={Col} md="4" lg="3">
                        <ProductImage
                            data-testid="img_product_image"
                            src={
                                data.image_url
                                    ? data.image_url
                                    : imagePlaceHolder
                            }
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = imagePlaceHolder;
                            }}
                            rounded
                        />
                    </Form.Group>
                    <Form.Group as={Col} md="8" lg="9">
                        <Row>
                            <FloatingLabel
                                controlId="product_name"
                                label="Product Name"
                                className="mb-3"
                            >
                                <Form.Control
                                    type="text"
                                    data-testid="input_product_name"
                                    readOnly
                                    plaintext="true"
                                    name="product_name"
                                    defaultValue={data.product_name}
                                    style={{
                                        color: isDarkMode ? "white" : "black",
                                        backgroundColor: "transparent",
                                        border: "none",
                                    }}
                                />
                            </FloatingLabel>
                        </Row>

                        {data.tags?.length > 0 && (
                            <Row>
                                <Stack
                                    direction="horizontal"
                                    gap={1}
                                    className="mb-3 flex-wrap"
                                >
                                    {data.tags.map((tag) => (
                                        <Badge
                                            key={tag.name}
                                            bg="secondary"
                                            style={{ fontWeight: "normal" }}
                                        >
                                            {tag.name}
                                        </Badge>
                                    ))}
                                </Stack>
                            </Row>
                        )}
                    </Form.Group>
                </Row>
            </ReactRouterForm>
            {[].concat(data.item_events).concat(data.tracking_events).length >
                0 && (
                <section id="events">
                    <hr />
                    <ListGroup as="div" className="my-3">
                        {[]
                            .concat(data.item_events)
                            .concat(data.tracking_events)
                            .map((event) => (
                                <EventDetail
                                    key={event.id}
                                    {...event}
                                    isDarkMode={isDarkMode}
                                    productUrl={data.product_url}
                                />
                            ))}
                    </ListGroup>
                </section>
            )}
            <ConfirmCancelDialog
                show={showDialog}
                callback={handleConfirmDelete}
            >
                Are you sure to delete this item?
            </ConfirmCancelDialog>
        </>
    );
};

export default ViewDetailForm;
